import React, { useState } from 'react';
import './memory.css';

import { PROFILES_PERMISSIONS } from '../../../permissions';
import checkPermissions from '../CheckPermissions';
import { MemoryForm } from './MemoryForm';
import { MemoryChange, MemoryItem } from './MemoryItem';
import {
  MemoryEntry,
  MemoryList,
  MemoryScope,
  NewMemory,
  asNewMemory,
  createMemory,
  groupMemory,
  replacedText,
  scopeOf,
} from './memoryApi';

interface Props {
  tenant: string;
  /** The model on screen, or null on the models page (every model, grouped). */
  modelId: string | null;
  list: MemoryList | null;
  error: string | null;
  onChanged: (change: MemoryChange) => void;
  /** Class for the outer element, so each chat can fit it to its own panel. */
  className?: string;
}

/**
 * What the assistant has been told to remember, and what is waiting to be decided.
 *
 * Replaces the transcript while open, the same way "My sessions" does: it is a place you
 * go and come back from, and the header control says Back while you are in it.
 */
export function MemoryDrawer({
  tenant,
  modelId,
  list,
  error,
  onChanged,
  className,
}: Props) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const canEdit = !!list?.can_edit;
  const groups = groupMemory(list?.entries ?? [], modelId);

  const scopes: { value: MemoryScope; label: string }[] = modelId
    ? [
        { value: 'model', label: 'This model' },
        { value: 'tenant', label: 'All models' },
      ]
    : [{ value: 'tenant', label: 'All models' }];

  const add = async (entry: NewMemory) => {
    setBusy(true);
    setFailed(null);
    try {
      const created = await createMemory(tenant, entry);
      setAdding(false);
      onChanged({
        entry: created,
        affectsNotes: scopeOf(created) === 'model',
        notesSynced: created.notes_synced ?? null,
      });
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  const section = (
    title: string,
    entries: MemoryEntry[],
    key: string,
    overrideFor?: string | null
  ) =>
    entries.length > 0 && (
      <section className="mem-section" key={key}>
        <h6 className="mem-section-title">
          {title}
          <span className="mem-count">{entries.length}</span>
        </h6>
        <ul className="mem-list">
          {entries.map((entry) => (
            <MemoryItem
              key={entry.entry_id}
              tenant={tenant}
              entry={entry}
              canEdit={canEdit}
              overrideFor={overrideFor}
              replaces={replacedText(entry, list?.entries ?? [])}
              onChanged={onChanged}
            />
          ))}
        </ul>
      </section>
    );

  // One group per source model, nearest first, the order rgip returns them in. Each
  // entry there can be overridden for this model; it stays as it is on its source.
  const inherited = new Map<string, MemoryEntry[]>();
  groups.inherited.forEach((entry) => {
    const from = entry.inherited_from_model_id as string;
    inherited.set(from, [...(inherited.get(from) ?? []), entry]);
  });

  const empty =
    list !== null &&
    groups.proposed.length +
      groups.tenant.length +
      groups.model.length +
      groups.inherited.length +
      groups.byModel.length ===
      0;

  return (
    <div className={`mem-drawer ${className ?? ''}`} data-testid="memory-view">
      {error && <p className="mem-failed">{error}</p>}
      {list === null && !error && <p className="mem-empty">Loading…</p>}
      {empty && !adding && <p className="mem-empty">No decisions yet.</p>}

      {canEdit &&
        checkPermissions(
          PROFILES_PERMISSIONS.ARCHITECT,
          adding ? (
            <div className="mem-section">
              <MemoryForm
                scopes={scopes}
                submitLabel="Add"
                busy={busy}
                onCancel={() => setAdding(false)}
                onSubmit={(values) => add(asNewMemory(values, modelId))}
              />
              {failed && <p className="mem-failed">{failed}</p>}
            </div>
          ) : (
            <button
              type="button"
              className="mem-add"
              onClick={() => setAdding(true)}
            >
              Add a decision
            </button>
          )
        )}

      {section('Waiting for approval', groups.proposed, 'proposed')}
      {modelId && section('This model', groups.model, 'model')}
      {Array.from(inherited.entries()).map(([from, entries]) =>
        section(`From model ${from}`, entries, `from-${from}`, modelId)
      )}
      {section('All models', groups.tenant, 'tenant')}
      {groups.byModel.map(({ modelId: id, entries }) =>
        section(`Model ${id}`, entries, `model-${id}`)
      )}
    </div>
  );
}
